import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  UsersIcon,
  SparklesIcon,
  ChartBarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ServerIcon,
  CloudIcon,
  CogIcon
} from '@heroicons/react/24/outline';

const InfluenceHubAIPage: React.FC = () => {
  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="gradient-bg text-white relative overflow-hidden py-24">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full mb-6">
              <UsersIcon className="w-5 h-5" />
              <span className="text-sm font-semibold">AI-Powered Marketing Platform</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Transform Influencer Marketing with <span className="text-pink-300">AI</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto mb-8 leading-relaxed">
              AI-powered influencer marketing platform for YouTube and Instagram. Self-hosted solution—own your data, customize features, no monthly SaaS fees.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/schedule-meeting"
                className="inline-flex items-center space-x-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                <span>Request Demo</span>
              </Link>
              <a
                href="#features"
                className="inline-flex items-center space-x-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-200"
              >
                <span>Explore Features</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Self-Hosted Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Self-Hosted Instead of SaaS?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-red-50 p-8 rounded-2xl border-l-4 border-red-500"
            >
              <h3 className="text-2xl font-bold text-red-600 mb-4">❌ The SaaS Problem</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 text-xl">•</span>
                  <span><strong>Forever Fees:</strong> $199-999/month = $2.4K-12K/year, every year</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 text-xl">•</span>
                  <span><strong>No Control:</strong> Limited features, forced updates, vendor roadmap</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 text-xl">•</span>
                  <span><strong>Data Hostage:</strong> Your influencer relationships on vendor servers</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 text-xl">•</span>
                  <span><strong>Scaling Costs:</strong> Per-user fees, per-campaign limits</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-green-50 p-8 rounded-2xl border-l-4 border-green-500"
            >
              <h3 className="text-2xl font-bold text-green-600 mb-4">✅ InfluenceHub AI Solution</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">•</span>
                  <span><strong>Own It:</strong> One-time license or affordable annual subscription</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">•</span>
                  <span><strong>Full Control:</strong> Complete source code for customization</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">•</span>
                  <span><strong>Your Data:</strong> Deploy on your infrastructure</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-3 text-xl">•</span>
                  <span><strong>Fixed Costs:</strong> No per-user or per-campaign fees</span>
                </li>
              </ul>
            </motion.div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-pink-50 to-purple-50 p-8 rounded-2xl border-2 border-pink-200 text-center">
            <p className="text-2xl font-bold text-gray-900 mb-2">Example Cost Comparison</p>
            <p className="text-lg text-gray-700">
              SaaS competitor: <span className="text-red-600 font-bold">$499/month = $6,000/year forever</span>
            </p>
            <p className="text-lg text-gray-700 mt-2">
              InfluenceHub self-hosted: <span className="text-green-600 font-bold">One-time license + $50/month hosting</span>
            </p>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Core Capabilities
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <ChartBarIcon className="w-12 h-12 text-pink-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Campaign Management</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-pink-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Create & manage multiple campaigns</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-pink-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Budget allocation and ROI tracking</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-pink-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Timeline planning with milestones</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-pink-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Real-time performance dashboard</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <UsersIcon className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Influencer Discovery</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>YouTube Data API v3 integration</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Instagram Graph API integration</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Real-time statistics & engagement</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Advanced filtering & search</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <SparklesIcon className="w-12 h-12 text-indigo-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">AI-Powered Insights</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>ChatGPT-4 integration</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Comment sentiment analysis</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Campaign performance summaries</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Influencer recommendations</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <DocumentTextIcon className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Contract Management</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Template-based contracts</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Deliverable specifications</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Payment terms & milestones</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Contract repository & tracking</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <svg className="w-12 h-12 text-green-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Pricing Management</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Track historical pricing</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>CPE & CPM calculations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Quote management</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Budget impact analysis</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg"
            >
              <ChartBarIcon className="w-12 h-12 text-orange-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Performance Analytics</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Real-time YouTube & Instagram analytics</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Video & post performance tracking</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Engagement metrics & trends</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>ROI calculations</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Perfect For Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Perfect For Multiple Business Models
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-pink-50 to-pink-100 p-8 rounded-xl border-2 border-pink-200"
            >
              <div className="text-4xl mb-4">🚀</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Startups & Growing Brands</h3>
              <p className="text-gray-700 mb-4">Launch influencer marketing without monthly SaaS fees eating into your budget.</p>
              <ul className="space-y-2 text-gray-700">
                <li>• One-time license fee</li>
                <li>• Deploy on cheap hosting ($20-100/month)</li>
                <li>• No per-campaign limits</li>
                <li>• Scale without cost increases</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-50 to-purple-100 p-8 rounded-xl border-2 border-purple-200"
            >
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Marketing Agencies</h3>
              <p className="text-gray-700 mb-4">License once, serve unlimited clients. White-label for your agency.</p>
              <ul className="space-y-2 text-gray-700">
                <li>• Use for all clients internally</li>
                <li>• Deploy per-client instances</li>
                <li>• Completely white-label</li>
                <li>• Add your own features</li>
                <li>• Bill clients for "your" platform</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-8 rounded-xl border-2 border-indigo-200"
            >
              <div className="text-4xl mb-4">🏢</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Enterprise Brands</h3>
              <p className="text-gray-700 mb-4">Complete data sovereignty and customization for your workflows.</p>
              <ul className="space-y-2 text-gray-700">
                <li>• Deploy on-premises or private cloud</li>
                <li>• Complete data control</li>
                <li>• Integrate with internal systems</li>
                <li>• Customize to your processes</li>
                <li>• Long-term cost savings</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Self-Hosted Model */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Self-Hosted License Model
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              How it works: You host, you own, you customize
            </p>
          </div>

          <div className="bg-white p-12 rounded-2xl shadow-xl">
            <div className="space-y-8">
              <div className="flex items-start">
                <div className="bg-pink-100 text-pink-600 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0 mr-4">1</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Purchase License</h3>
                  <p className="text-gray-700">One-time perpetual or annual subscription</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-purple-100 text-purple-600 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0 mr-4">2</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Receive Source Code</h3>
                  <p className="text-gray-700">Complete application (React frontend + Node.js backend + PostgreSQL)</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-indigo-100 text-indigo-600 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0 mr-4">3</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Deploy on Your Infrastructure</h3>
                  <p className="text-gray-700">Your servers, your cloud account (AWS/Azure/GCP), or on-premises</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-blue-100 text-blue-600 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0 mr-4">4</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Customize as Needed</h3>
                  <p className="text-gray-700">Modify features, add integrations, adapt workflows to your business</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-green-100 text-green-600 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl flex-shrink-0 mr-4">5</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Use Your API Keys</h3>
                  <p className="text-gray-700">YouTube, Instagram, OpenAI quotas under your control</p>
                </div>
              </div>
            </div>

            <div className="mt-12 grid md:grid-cols-2 gap-8">
              <div className="bg-green-50 p-6 rounded-xl">
                <h4 className="font-bold text-green-900 mb-3">✅ What You Get:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• You host the application</li>
                  <li>• You control your data</li>
                  <li>• You can customize the code</li>
                  <li>• You manage your infrastructure</li>
                  <li>• QuickReach provides updates</li>
                </ul>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl">
                <h4 className="font-bold text-blue-900 mb-3">🔒 QuickReach Retains:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Intellectual property rights</li>
                  <li>• Core platform ownership</li>
                  <li>• You get license to use & modify</li>
                  <li>• Cannot resell the software itself</li>
                  <li>• Can offer services using it</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Modern Technology Stack
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-xl">
              <CogIcon className="w-12 h-12 text-pink-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Frontend</h3>
              <p className="text-gray-700">React - Modern JavaScript framework for responsive UI</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <ServerIcon className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Backend</h3>
              <p className="text-gray-700">Node.js + Express - Scalable API server</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <CloudIcon className="w-12 h-12 text-indigo-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Database</h3>
              <p className="text-gray-700">PostgreSQL or SQLite - Reliable data storage</p>
            </div>
          </div>

          <div className="mt-8 bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-xl">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Integrations:</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg">
                <p className="font-semibold">📺 YouTube Data API v3</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="font-semibold">📷 Instagram Graph API</p>
              </div>
              <div className="bg-white p-4 rounded-lg">
                <p className="font-semibold">🤖 OpenAI ChatGPT-4</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-pink-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Own Your Influencer Marketing Platform?
          </h2>
          <p className="text-xl mb-8">
            Stop paying monthly SaaS fees. Own your data. Customize your workflows.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/schedule-meeting"
              className="inline-flex items-center space-x-2 bg-white text-pink-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <span>Request a Demo</span>
            </Link>
            <Link
              to="/schedule-meeting"
              className="inline-flex items-center space-x-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-pink-600 transition-all"
            >
              <span>Get Custom Quote</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InfluenceHubAIPage;

